import { useState, useEffect } from 'react';
import { getPaymentPlans } from '@shared/services/services';
import { currency } from '@shared/hooks/currencies';

export const usePaymentMethod = ({
  precio,
  idPlan,
  isInUSD = false,
  setCardData,
  handleChangeInfoCard,
  cardData,
  setPriceDetail: externalSetPriceDetail,
  initialPriceDetail = {},
}) => {
  const [selectedValueRadio, setSelectedValueRadio] = useState('');
  const handleChangeRadio = value => {
    setSelectedValueRadio(value);
  };

  const [mediosDePago, setMediosDePago] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState({});
  const [pagoDeb, setPagoDeb] = useState(null);
  const [pagoCred, setPagoCred] = useState(null);
  const [pagoCuot, setPagoCuot] = useState(null);
  const [pagoValue, setPagoValue] = useState(null);

  const [internalPriceDetail, setInternalPriceDetail] = useState(
    initialPriceDetail
  );
  const setPriceDetail = externalSetPriceDetail || setInternalPriceDetail;
  const priceDetail = externalSetPriceDetail
    ? initialPriceDetail
    : internalPriceDetail;

  const pagoPrisma = datos => {
    handleChangeInfoCard({
      cardInfo: { ...datos },
      idPlan: selectedMethod?.pago?.id,
      cardType: selectedMethod?.cardType,
    });
  };

  const setCardType = card => {
    setSelectedMethod({ ...selectedMethod, cardType: card });
  };

  const valorRecargo = (coeficiente, cuotas) => {
    if (coeficiente == null) return null;

    const totalMountToNumber = Number.parseFloat(precio?.total);

    if (!Number.isFinite(totalMountToNumber)) return null;

    const coef = Number(coeficiente);

    if (!Number.isFinite(coef)) return null;

    const safeCuotas = Number.isFinite(Number(cuotas)) ? Number(cuotas) : 0;

    const recargo = totalMountToNumber * coef - totalMountToNumber;

    const totalYRecargo = totalMountToNumber + recargo;

    const valorCuota = safeCuotas > 0 ? totalYRecargo / safeCuotas : 0;

    const newCardData = {
      ...cardData,
      priceTotal: totalMountToNumber,
      cuotasTotal: safeCuotas,
      recargo,
      valorCuota,
      totalYRecargo,
    };

    setCardData(newCardData);

    setPriceDetail({
      ...priceDetail,
      recargo: recargo.toFixed(2),
      totalCuotas: safeCuotas,
      montoCuotas: valorCuota.toFixed(2),
      finalPrice: totalYRecargo.toFixed(2),
      totalValue: totalMountToNumber,
    });

    return newCardData;
  };

  const setPago = pago => {
    const numberOfPayments =
      pago.installment?.number_of_payments || pago?.number_of_payments || 1;

    valorRecargo(pago.coefficient, numberOfPayments);

    setSelectedMethod({ ...selectedMethod, pago });
  };

  const setCuota = cuota => {
    if (cuota === undefined) {
      setSelectedMethod({ ...selectedMethod, cuota });
    } else {
      setSelectedMethod({
        ...selectedMethod,
        pago: undefined,
        cuota,
      });
    }
  };

  const handleSelectDebito = item => {
    setPagoDeb(item);
    setPagoValue(item?.original);
    setPago(item?.original);
  };

  const handleChangeCrd = item => {
    setPagoCred(item);
    setPagoValue(item?.original);
    setCuota(item?.original);
    setPagoCuot(undefined);
    setCardData(undefined);
  };

  const handleChangeCuot = item => {
    setPagoCuot(item);
    setPagoValue(item?.original);
    setPago(item?.original);
  };

  const creditMethodText = obj => {
    const numberOfPayments = obj.installmentInfo.number_of_payments;
    const montoCoef =
      (Number.parseFloat(precio?.total) * obj.planes[0].coefficient) /
      numberOfPayments;

    const montoFormateado = currency(montoCoef, isInUSD);
    const suffix = numberOfPayments === 1 ? '.' : ' c/u.';

    return `A partir de ${montoFormateado}${suffix}`;
  };

  const formatInstallmentText = (obj, precio) => {
    const numberOfPayments = obj?.installment?.number_of_payments ?? 1;
    const coefficient = obj?.coefficient ?? 0;
    const total = precio?.total ?? 0;

    const montoCoef = (total * coefficient) / numberOfPayments;
    const montoFormateado = currency(montoCoef, isInUSD);

    if (numberOfPayments === 1) {
      return [
        { text: `${numberOfPayments} cuota`, bold: true },
        { text: ` de ${montoFormateado}.`, bold: false },
      ];
    }

    return [
      { text: `${numberOfPayments} cuotas`, bold: true },
      { text: ` de ${montoFormateado} c/u.`, bold: false },
    ];
  };

  const interestAmount = obj => {
    const montoCoef =
      Number.parseFloat(precio?.total) * Number.parseFloat(obj.coefficient);
    return montoCoef - Number.parseFloat(precio?.total);
  };

  const getTotal = (coeficiente, total) => coeficiente * total;

  const handleBoxClick = value => {
    handleChangeRadio(value);
    setCardType(value);
    setPagoCred(undefined);
    setPagoCuot(undefined);
    setPagoDeb(undefined);
    setCardData(undefined);
    setPriceDetail({
      ...priceDetail,
      recargo: undefined,
      totalCuotas: undefined,
      montoCuotas: undefined,
      finalPrice: undefined,
    });
  };

  const debitoItems = mediosDePago?.debito?.map((item, i) => ({
    id: i,
    fragments: [
      { text: '1 pago', bold: true },
      { text: ' de', bold: false },
      {
        text: currency(getTotal(item.coefficient, precio?.total), isInUSD),
        bold: true,
      },
    ],
    icon: item.payment_type?.logo ?? null,
    original: item,
  }));

  const creditoItems = Object.keys(mediosDePago?.credito ?? {}).map(key => {
    const planGroup = mediosDePago?.credito[key];
    const numberOfPayments =
      planGroup?.installmentInfo?.number_of_payments || 1;

    return {
      id: key,
      fragments: [
        {
          text: `${numberOfPayments} cuota${numberOfPayments > 1 ? 's' : ''}`,
          bold: true,
        },
        { text: ` - ${creditMethodText(planGroup)}`, bold: false },
      ],
      icon: planGroup?.installmentInfo?.logo ?? null,
      original: planGroup,
    };
  });

  const creditoDropdownItems =
    selectedMethod?.cuota?.planes?.map((item, i) => ({
      id: i,
      icon: item.payment_type?.logo ?? null,
      mobileWithIcon: true,
      fragments: [
        ...formatInstallmentText(item, precio),
        {
          text: ` Total ${currency(
            getTotal(item.coefficient, precio?.total),
            isInUSD
          )}`,
          bold: true,
        },
        {
          text: `- Recargo ${currency(interestAmount(item), isInUSD)}`,
          bold: true,
        },
      ],
      original: item,
    })) || [];

  useEffect(() => {
    const fetchData = async () => {
      const result = await getPaymentPlans(idPlan);

      const paymentPlansFormatted = result.reduce(
        (acum, act) => {
          const type = act.payment_type.payment_method_type.toLowerCase();
          acum[type] = [...(acum[type] || []), act];
          return acum;
        },
        { credito: [], debito: [] }
      );

      const installmentGrouped = paymentPlansFormatted.credito.reduce(
        (acum, plan) => {
          const key = `installment_${plan.installment.installments}`;
          if (acum[key]) {
            acum[key].planes.push(plan);
          } else {
            acum[key] = {
              installmentInfo: { ...plan.installment },
              planes: [plan],
            };
          }
          return acum;
        },
        {}
      );

      setMediosDePago({
        credito: installmentGrouped,
        debito: paymentPlansFormatted.debito,
      });
    };

    fetchData();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const updatePricesAndCardData = () => {
      setPriceDetail(prevPricesDetail => ({
        ...prevPricesDetail,
        totalValue: precio?.total,
        impPais: precio?.rates?.impuestoO5,
        impRg: precio?.rates?.RG5450,
        impRg5430: precio?.rates?.RG5430,
      }));

      if (pagoValue) {
        if (isMounted) {
          valorRecargo(
            pagoValue.coefficient,
            pagoValue?.installment?.number_of_payments
          );
          setSelectedMethod(prevSelectedMethod => ({
            ...prevSelectedMethod,
            pago: pagoValue,
          }));
        }
      }
    };

    updatePricesAndCardData();

    return () => {
      isMounted = false;
    };
  }, [precio, pagoValue]);

  return {
    selectedValueRadio,
    handleChangeRadio,
    mediosDePago,
    pagoDeb,
    pagoCred,
    pagoCuot,
    pagoValue,
    pagoPrisma,
    handleChangeCrd,
    handleSelectDebito,
    handleChangeCuot,
    handleBoxClick,
    debitoItems,
    creditoItems,
    creditoDropdownItems,
    setCardType,
    setPagoCred,
    setPagoCuot,
    setPagoDeb,
  };
};
